/**
 * SuggestInput — a text input with type-ahead suggestions fetched from an
 * endpoint. Users can always ignore the suggestions and type anything.
 *
 * Props:
 *   value:          current string value
 *   onChange(v):    called on every keystroke or when the user picks a suggestion
 *   endpoint:       API path (relative to /api). Called as `${endpoint}?q=&limit=8`
 *                   plus any extra fields in `params`.
 *   params:         optional query-param dict merged with `q`
 *   placeholder, label, className, disabled, testid, minChars
 */
import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";

export default function SuggestInput({
  value = "",
  onChange,
  endpoint,
  params = {},
  placeholder,
  className = "",
  disabled = false,
  testid,
  minChars = 1,
}) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [hi, setHi] = useState(-1);   // keyboard-highlighted index
  const wrapRef = useRef(null);
  const debRef = useRef(null);

  // Debounced suggestion fetch.
  useEffect(() => {
    if (!endpoint) return;
    if (disabled) return;
    if ((value || "").length < minChars) { setItems([]); return; }
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const qs = new URLSearchParams({ q: value, limit: "8", ...params }).toString();
        const { data } = await http.get(`${endpoint}?${qs}`);
        setItems(Array.isArray(data?.items) ? data.items : []);
      } catch {
        setItems([]);
      } finally { setBusy(false); }
    }, 200);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, endpoint, disabled]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (v) => {
    onChange?.(v);
    setOpen(false);
    setHi(-1);
  };

  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && hi >= 0) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange?.(e.target.value); setOpen(true); setHi(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="w-full px-3 py-2 rounded-lg border bg-transparent text-sm"
        style={{ borderColor: "var(--wofy-border)" }}
        data-testid={testid}
      />
      {open && items.length > 0 && (
        <ul
          className="absolute left-0 right-0 z-30 mt-1 rounded-lg border bg-white shadow-lg max-h-60 overflow-auto"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={testid && `${testid}-list`}
        >
          {items.map((it, i) => (
            <li
              key={`${it}-${i}`}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              onMouseEnter={() => setHi(i)}
              className="px-3 py-2 text-sm cursor-pointer"
              style={{ background: i === hi ? "var(--wofy-surface)" : "transparent" }}
              data-testid={testid && `${testid}-item-${i}`}
            >
              {it}
            </li>
          ))}
        </ul>
      )}
      {open && !busy && items.length === 0 && (value || "").length >= minChars && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 rounded-lg border bg-white shadow-sm px-3 py-2 text-xs opacity-70"
          style={{ borderColor: "var(--wofy-border)" }}
          data-testid={testid && `${testid}-empty`}
        >
          No matches — press Enter to keep what you typed.
        </div>
      )}
    </div>
  );
}
